/*
 * Wear the Legends — landing page runtime for the WTL sections.
 *
 * One file serves every section, so a section added in the theme editor finds its
 * code already loaded. Each section module registers against its data-wtl-section
 * name, is started for every matching element, and is started again or torn down
 * on the theme editor's shopify:section:load / shopify:section:unload events.
 *
 * The allegiance (Saruman or Sauron) is shared page state: WTL.choose() stores it,
 * mirrors it on <body data-allegiance>, and announces it with a wtl:allegiance event.
 */
(() => {
  'use strict';
  if (window.WTL) return;

  const SIDES = ['saruman', 'sauron'];
  const STORAGE_KEY = 'loreti-two-powers-allegiance-v1';
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const modules = {};
  const running = new WeakMap();
  let selected = null;
  let toastTimer = 0;

  const WTL = {
    // Names and titles; the timepieces section replaces these with its own settings.
    sides: {
      saruman: { name: 'Saruman', title: 'The White Hand' },
      sauron: { name: 'Sauron', title: 'The Red Eye' }
    },
    reducedMotion,
    get selected() { return selected; },
    isSide(side) { return SIDES.includes(side); },

    choose(side, { notify = true, persist = true, reveal = true } = {}) {
      if (!WTL.isSide(side)) return false;
      selected = side;
      if (persist) { try { localStorage.setItem(STORAGE_KEY, side); } catch (_) {} }
      document.body.dataset.allegiance = side;
      document.querySelectorAll('[data-select]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.select === side));
      });
      document.dispatchEvent(new CustomEvent('wtl:allegiance', { detail: { side, reveal, notify } }));
      const choice = WTL.sides[side];
      if (notify) WTL.toast(`${choice.name} selected. ${choice.title}.`);
      return true;
    },

    toast(message) {
      const toast = document.querySelector('[data-wtl-toast]');
      if (!toast) return;
      clearTimeout(toastTimer);
      toast.textContent = message;
      toast.classList.add('visible');
      toastTimer = setTimeout(() => toast.classList.remove('visible'), 3200);
    },

    // Scrolls to an in-page anchor, or follows any other link.
    go(href) {
      if (!href) return;
      if (href.charAt(0) === '#') {
        const target = document.getElementById(href.slice(1));
        if (target) target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      } else window.location.href = href;
    },

    register(name, init) {
      modules[name] = init;
      document.querySelectorAll(`[data-wtl-section="${name}"]`).forEach(start);
    }
  };
  window.WTL = WTL;

  function start(root) {
    const init = modules[root.dataset.wtlSection];
    if (!init || running.has(root)) return;
    const lifecycle = new AbortController();
    const cleanup = init(root, lifecycle.signal);
    running.set(root, () => { lifecycle.abort(); if (typeof cleanup === 'function') cleanup(); });
  }
  function stop(root) {
    const cleanup = running.get(root);
    if (cleanup) { cleanup(); running.delete(root); }
  }
  function within(container, fn) {
    if (!container) return;
    if (container.matches?.('[data-wtl-section]')) fn(container);
    container.querySelectorAll?.('[data-wtl-section]').forEach(fn);
  }

  // Any [data-select] control on the page swears an allegiance.
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-select]');
    if (button && WTL.isSide(button.dataset.select)) WTL.choose(button.dataset.select);
  });

  document.addEventListener('shopify:section:load', event => within(event.target, start));
  document.addEventListener('shopify:section:unload', event => within(event.target, stop));

  // ---- page chrome: header state, allegiance bar, reading progress, reveals ----
  // The bar shows once a side is chosen, except over the hero and over the sections
  // marked data-wtl-hides-choice-bar. Visibility comes from IntersectionObserver, so it
  // also works on themes that scroll the page with a smooth-scroll library (no window
  // scroll events) or that transform <main>.
  const debug = /[?&]wtl_debug\b/.test(location.search);
  const inView = new Set();
  let watcher = null;
  let lastReason = '';
  function watchChoiceBarTargets() {
    if (!('IntersectionObserver' in window)) return;
    if (!watcher) {
      watcher = new IntersectionObserver(entries => {
        entries.forEach(entry => entry.isIntersecting ? inView.add(entry.target) : inView.delete(entry.target));
        updateChoiceBar();
      }, { rootMargin: '-100px 0px 0px 0px' });
    }
    document.querySelectorAll('[data-wtl-section="hero"], [data-wtl-hides-choice-bar]').forEach(element => {
      if (element.dataset.wtlWatched) return;
      element.dataset.wtlWatched = 'true';
      watcher.observe(element);
    });
  }
  function isShown(element) {
    if (!element.isConnected) { inView.delete(element); return false; }
    if (watcher) return inView.has(element);
    const rect = element.getBoundingClientRect();
    return rect.top < innerHeight && rect.bottom > 100;
  }
  function updateChoiceBar() {
    const bar = document.querySelector('[data-wtl-choice-bar]');
    let reason = '';
    if (!bar) reason = 'no WTL · Allegiance bar section on this page';
    else if (!selected && bar.dataset.showUnchosen !== 'true') reason = 'no side chosen yet (turn on "Show before a side is chosen" to show it anyway)';
    else {
      const blocker = [...document.querySelectorAll('[data-wtl-section="hero"], [data-wtl-hides-choice-bar]')].find(isShown);
      if (blocker) reason = `hidden over #${blocker.id || blocker.dataset.wtlSection}`;
    }
    if (bar) {
      bar.hidden = Boolean(reason);
      const name = bar.querySelector('[data-wtl-choice-name]');
      if (name) name.textContent = selected ? WTL.sides[selected].name.toUpperCase() : bar.dataset.unchosenName;
      const change = bar.querySelector('[data-wtl-choice-change]');
      if (change) change.textContent = selected ? change.dataset.chosen : (change.dataset.unchosen || change.dataset.chosen);
    }
    if (debug && reason !== lastReason) {
      lastReason = reason;
      if (!reason && bar) {
        const rect = bar.getBoundingClientRect();
        const top = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
        console.info('[WTL] allegiance bar shown', rect, top && !bar.contains(top) ? ['but covered by', top] : '');
      } else console.info('[WTL] allegiance bar hidden:', reason);
    }
  }

  let progress = null;
  function updateProgress() {
    if (!progress) return;
    const max = document.documentElement.scrollHeight - innerHeight;
    progress.style.transform = `scaleX(${max > 0 ? Math.min(1, Math.max(0, scrollY / max)) : 0})`;
  }

  let scrollFrame = 0;
  WTL.refresh = () => onScroll();
  function onScroll() {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      document.querySelectorAll('[data-wtl-header]').forEach(header => header.classList.toggle('scrolled', scrollY > 35));
      updateChoiceBar();
      updateProgress();
      scrollFrame = 0;
    });
  }

  const REVEAL = '.lx__h,.lx__lede,.launch-heading,.bundle-card,.houses__hd,.houses__grid,.macro-hd,.section-top,.pal__copy,.closing-inner';
  let revealObserver = null;
  function prepareReveals(container) {
    if (!revealObserver || !container?.querySelectorAll) return;
    container.querySelectorAll(REVEAL).forEach(element => {
      if (element.classList.contains('reveal-ready')) return;
      element.classList.add('reveal-ready');
      revealObserver.observe(element);
    });
  }

  function boot() {
    if (document.querySelector('[data-wtl-section]')) {
      progress = document.createElement('div');
      progress.className = 'reading-progress';
      progress.setAttribute('aria-hidden', 'true');
      // the progress bar is styled inside the scoped wrapper
      const holder = document.createElement('div');
      holder.className = 'wtl-s wtl-progress-holder';
      holder.append(progress);
      document.body.append(holder);
    }
    // the theme editor keeps sections visible while they are edited
    if ('IntersectionObserver' in window && !reducedMotion.matches && !window.Shopify?.designMode) {
      revealObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); revealObserver.unobserve(entry.target); }
      }), { threshold: 0.08 });
      prepareReveals(document);
      document.addEventListener('shopify:section:load', event => prepareReveals(event.target));
      reducedMotion.addEventListener('change', () => {
        if (reducedMotion.matches) document.querySelectorAll('.reveal-ready').forEach(element => element.classList.add('is-visible'));
      });
    }

    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll, { passive: true });
    document.addEventListener('wtl:allegiance', onScroll);
    watchChoiceBarTargets();
    document.addEventListener('shopify:section:load', () => { watchChoiceBarTargets(); onScroll(); });
    document.fonts?.ready.then(onScroll);

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (WTL.isSide(saved)) WTL.choose(saved, { notify: false, persist: false, reveal: false });
    } catch (_) {}
    onScroll();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();

/* ============================================================
   HERO - two photographs split by pointer or keyboard, and the
   countdown to the public release.
   ============================================================ */
WTL.register('hero', (hero, signal) => {
  const slider = hero.querySelector('[data-hero-reveal]');
  const media = hero.querySelector('.hero-media');
  const saruman = hero.querySelector('[data-hero-photo="saruman"]');
  const sauron = hero.querySelector('[data-hero-photo="sauron"]');
  const reducedMotion = WTL.reducedMotion;
  const hover = matchMedia('(hover: hover) and (pointer: fine)');
  let current = 50;
  let target = 50;
  let frame = 0;
  let lastTime = 0;
  let gesture = null;

  function paint() { hero.style.setProperty('--hero-split', `${current.toFixed(3)}%`); }
  function animate(time) {
    const elapsed = lastTime ? Math.min(40, time - lastTime) : 16;
    lastTime = time;
    current += (target - current) * (1 - Math.exp(-elapsed / 65));
    if (Math.abs(target - current) < 0.03) {
      current = target; frame = 0; lastTime = 0; paint();
      return;
    }
    paint();
    frame = requestAnimationFrame(animate);
  }
  function setSplit(value, smooth = true) {
    if (!Number.isFinite(value)) return;
    target = Math.max(0, Math.min(100, value));
    slider.value = String(Math.round(target));
    slider.setAttribute('aria-valuetext', `${Math.round(target)} percent ${WTL.sides.saruman.name}, ${100 - Math.round(target)} percent ${WTL.sides.sauron.name}`);
    hero.dataset.world = target === 100 ? 'saruman' : target === 0 ? 'sauron' : 'both';
    saruman.setAttribute('aria-hidden', String(target === 0));
    sauron.setAttribute('aria-hidden', String(target === 100));
    if (!smooth || reducedMotion.matches) {
      cancelAnimationFrame(frame);
      frame = 0; lastTime = 0; current = target;
      paint();
    } else if (!frame) {
      frame = requestAnimationFrame(animate);
    }
  }
  function positionAt(clientX) {
    const bounds = media.getBoundingClientRect();
    return bounds.width > 0 ? (clientX - bounds.left) / bounds.width * 100 : 50;
  }
  function isControl(element) {
    return Boolean(element.closest('a, button, input, select, textarea, summary'));
  }

  // The visually hidden native range supplies keyboard support. Pointer gestures use the photograph itself.
  slider.addEventListener('input', () => setSplit(Number(slider.value), false), { signal });
  slider.addEventListener('pointerdown', () => { gesture = null; }, { signal });

  // Hover tracks position, not movement, so either image can be fully revealed.
  hero.addEventListener('pointermove', event => {
    if (event.pointerType === 'mouse' && hover.matches && !event.buttons) {
      if (isControl(event.target) && event.target !== slider) return;
      setSplit(positionAt(event.clientX));
      return;
    }
    if (!gesture || event.pointerId !== gesture.id) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    if (!gesture.dragging) {
      if (Math.abs(dy) > 9 && Math.abs(dy) >= Math.abs(dx)) { gesture = null; return; }
      if (Math.abs(dx) < 7 || Math.abs(dx) <= Math.abs(dy)) return;
      gesture.dragging = true;
      hero.setPointerCapture(event.pointerId);
    }
    setSplit(positionAt(event.clientX), false);
  }, { signal });

  // Horizontal photo dragging works on touch; vertical page scrolling stays native.
  hero.addEventListener('pointerdown', event => {
    if (event.isPrimary === false || isControl(event.target) || (event.pointerType === 'mouse' && event.button !== 0)) return;
    gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, dragging: false };
    if (event.pointerType === 'mouse') setSplit(positionAt(event.clientX), false);
  }, { signal });
  function endGesture(event) {
    if (gesture?.id !== event.pointerId) return;
    if (hero.hasPointerCapture(event.pointerId)) hero.releasePointerCapture(event.pointerId);
    gesture = null;
  }
  ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => hero.addEventListener(type, endGesture, { signal }));
  hero.addEventListener('pointerleave', event => { if (gesture && !gesture.dragging) endGesture(event); }, { signal });
  reducedMotion.addEventListener('change', () => setSplit(target, false), { signal });

  // A sworn allegiance reveals that watch without moving the page.
  document.addEventListener('wtl:allegiance', event => {
    if (event.detail.reveal) setSplit(event.detail.side === 'saruman' ? 100 : 0);
  }, { signal });
  setSplit(50, false);

  // ---- countdown ----
  const countdown = hero.querySelector('.hero-countdown');
  const releaseAt = countdown ? new Date(countdown.dataset.release).getTime() : NaN;
  let countdownTimer = 0;
  function updateCountdown() {
    const now = Date.now();
    const total = Math.max(0, Math.floor((releaseAt - now) / 1000));
    const parts = { days: Math.floor(total / 86400), hours: Math.floor(total / 3600) % 24, minutes: Math.floor(total / 60) % 60, seconds: total % 60 };
    Object.entries(parts).forEach(([unit, value]) => {
      const number = countdown.querySelector(`[data-cd="${unit}"]`);
      const text = String(value).padStart(2, '0');
      if (!number || number.textContent === text) return;
      const firstPaint = number.textContent === '--';
      number.textContent = text;
      if (!firstPaint && !reducedMotion.matches && number.animate) number.animate([
        { opacity: .45, transform: 'translateY(5px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], { duration: 360, easing: 'cubic-bezier(.18,.8,.22,1)' });
    });
    countdown.style.setProperty('--second-position', `${parts.seconds / 60 * 100}%`);
    const title = countdown.querySelector('[data-cd-title]');
    if (title) title.textContent = now >= releaseAt ? title.dataset.after : title.dataset.before;
    if (now >= releaseAt) clearInterval(countdownTimer);
  }
  function startCountdown() {
    clearInterval(countdownTimer);
    updateCountdown();
    if (Date.now() < releaseAt) countdownTimer = setInterval(updateCountdown, 1000);
  }
  if (countdown && Number.isFinite(releaseAt)) {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { clearInterval(countdownTimer); gesture = null; setSplit(target, false); }
      else startCountdown();
    }, { signal });
    startCountdown();
  }

  return () => { cancelAnimationFrame(frame); clearInterval(countdownTimer); };
});

/* ============================================================
   TIMEPIECES - model tabs, specification accordions, and the
   packshot explorer with hotspots on the face and the caseback.
   Hotspot positions are percentages of the photograph.
   ============================================================ */
WTL.register('timepieces', (section, signal) => {
  const $ = selector => section.querySelector(selector);
  const $$ = selector => [...section.querySelectorAll(selector)];
  let data;
  try { data = JSON.parse($('[data-wtl-models]').textContent); } catch (_) { return; }
  Object.keys(data).forEach(side => {
    if (WTL.isSide(side)) WTL.sides[side] = { name: data[side].name, title: data[side].title };
  });

  const ns = 'http://www.w3.org/2000/svg';
  const stage = $('.product-image-wrap');
  const image = $('[data-product-image]');
  const overlay = $('.watch-hotspots');
  const tip = $('.watch-part-tip');
  const card = $('.watch-part-card');
  const caseback = $('.caseback-view');
  const specs = $('[data-spec-accordions]');
  const viewLabel = $('.product-view-label');
  const turnButton = $('.turn-watch');
  const chooseButton = $('[data-choose-model]');
  const compact = matchMedia('(max-width: 1000px)');
  const reducedMotion = WTL.reducedMotion;
  const labels = section.dataset;
  let model = data.saruman ? 'saruman' : Object.keys(data)[0];
  let view = 'watch';
  let turned = false;
  let layoutFrame = 0;
  let selected = null;
  let previewed = null;

  function watch() { return data[model]; }
  function points() {
    const list = view === 'caseback' ? watch().back : watch().front;
    return list.filter(point => watch().specs[point.spec]);
  }
  function media() {
    if (view === 'caseback') {
      const photo = caseback.querySelector('img');
      if (!photo) return null;
      if (!photo.complete || !photo.naturalWidth) {
        photo.addEventListener('load', scheduleFit, { once: true });
        return null;
      }
      return photo;
    }
    return image.complete && image.naturalWidth > 0 ? image : null;
  }
  function frameOf(element) { return [element.naturalWidth || 1000, element.naturalHeight || 1000]; }
  function fitOverlay() {
    layoutFrame = 0;
    const element = media();
    overlay.toggleAttribute('hidden', !element);
    if (!element) { tip.hidden = true; return; }
    const [frameW, frameH] = frameOf(element);
    const box = element.getBoundingClientRect();
    const parent = stage.getBoundingClientRect();
    // object-fit: contain fits the frame inside the responsive image element.
    const scale = Math.min(box.width / frameW, box.height / frameH);
    const width = frameW * scale;
    const height = frameH * scale;
    if (!width || !height) { overlay.setAttribute('hidden', ''); tip.hidden = true; return; }
    overlay.setAttribute('viewBox', `0 0 ${frameW} ${frameH}`);
    overlay.dataset.w = frameW;
    overlay.dataset.h = frameH;
    overlay.querySelectorAll('.watch-hotspot').forEach(button => {
      button.setAttribute('transform', `translate(${Number(button.dataset.x) / 100 * frameW} ${Number(button.dataset.y) / 100 * frameH})`);
    });
    overlay.style.width = `${width}px`;
    overlay.style.height = `${height}px`;
    overlay.style.left = `${box.left - parent.left + (box.width - width) / 2}px`;
    overlay.style.top = `${box.top - parent.top + (box.height - height) / 2}px`;
    // Keep the visible dots and their 44 px touch targets consistent at every size.
    overlay.querySelectorAll('.watch-point-circle').forEach(point => {
      point.setAttribute('r', String(Number(point.dataset.radius) * frameW / width));
    });
    restoreTip();
  }
  function scheduleFit() { if (!layoutFrame) layoutFrame = requestAnimationFrame(fitOverlay); }
  function showTip(part) {
    if (overlay.hasAttribute('hidden')) return;
    tip.textContent = watch().specs[part.spec][0].toUpperCase();
    tip.hidden = false;
    const parent = stage.getBoundingClientRect();
    const bounds = overlay.getBoundingClientRect();
    const x = bounds.left - parent.left + part.x / 100 * bounds.width;
    const y = bounds.top - parent.top + part.y / 100 * bounds.height;
    const half = tip.offsetWidth / 2 + 10;
    tip.style.left = `${Math.max(half, Math.min(parent.width - half, x))}px`;
    tip.style.top = `${Math.max(20, Math.min(parent.height - 15, y - 23))}px`;
  }
  function hideTip() { tip.hidden = true; }
  function restoreTip() {
    const part = points().find(item => item.spec === (previewed ?? selected));
    if (part && !overlay.hasAttribute('hidden')) showTip(part);
    else hideTip();
  }
  function renderPoints() {
    overlay.replaceChildren();
    overlay.setAttribute('aria-label', view === 'caseback'
      ? `Explore the caseback of the ${watch().name} watch`
      : `Explore the parts of the ${watch().name} watch`);
    points().forEach(part => {
      const name = watch().specs[part.spec][0];
      const button = document.createElementNS(ns, 'g');
      button.classList.add('watch-hotspot');
      button.dataset.spec = String(part.spec);
      button.dataset.x = String(part.x);
      button.dataset.y = String(part.y);
      button.setAttribute('role', 'button');
      button.setAttribute('tabindex', '0');
      button.setAttribute('aria-label', `${name}: open specifications`);
      button.setAttribute('aria-controls', `spec-${model}-${part.spec}`);
      button.setAttribute('aria-expanded', 'false');
      [['hit', 22], ['halo', 18], ['ring', 12], ['core', 3.5]].forEach(([kind, radius]) => {
        const point = document.createElementNS(ns, 'circle');
        point.classList.add('watch-point-circle', `watch-point-${kind}`);
        point.dataset.radius = String(radius);
        point.setAttribute('vector-effect', 'non-scaling-stroke');
        point.setAttribute('aria-hidden', 'true');
        button.append(point);
      });
      const preview = () => { previewed = part.spec; showTip(part); };
      const leave = () => { previewed = null; restoreTip(); };
      button.addEventListener('pointerenter', preview);
      button.addEventListener('pointerleave', leave);
      button.addEventListener('focus', preview);
      button.addEventListener('blur', leave);
      button.addEventListener('click', () => selectPart(part.spec));
      button.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); selectPart(part.spec); }
      });
      overlay.append(button);
    });
    scheduleFit();
  }
  function setActive(index, showCard = false) {
    selected = index;
    overlay.querySelectorAll('.watch-hotspot').forEach(button => {
      const active = Number(button.dataset.spec) === index;
      button.classList.toggle('is-selected', active);
      button.setAttribute('aria-expanded', String(active));
    });
    restoreTip();
    card.hidden = true;
    if (index === null || index === undefined) return;
    const [name, value, copy] = watch().specs[index];
    $('[data-part-name]').textContent = name;
    $('[data-part-value]').textContent = value;
    $('[data-part-copy]').textContent = copy;
    if (showCard) {
      $('[data-explorer-status]').textContent = `${name} specifications opened. ${value}.`;
      card.hidden = false;
      if (compact.matches) requestAnimationFrame(() => card.scrollIntoView({ block: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' }));
    }
  }
  function selectPart(index) {
    const detail = section.querySelector(`#spec-${model}-${index}`);
    if (!detail) return;
    if (!detail.open) { detail.dataset.fromWatch = 'true'; detail.open = true; }
    $$('.spec-detail').forEach(other => { if (other !== detail) other.open = false; });
    setActive(index, true);
  }
  function setView(kind) {
    view = kind;
    previewed = null;
    selected = null;
    card.hidden = true;
    hideTip();
    renderPoints();
  }

  // Turning the watch over shows the caseback photograph in the packshot's frame.
  function syncTurnCta() {
    turnButton.replaceChildren(document.createTextNode(`${turned ? labels.turnBack : labels.turnOver} `));
    const icon = document.createElement('span');
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '↻';
    turnButton.append(icon);
    turnButton.setAttribute('aria-pressed', String(turned));
  }
  function setCaseback(show) {
    turned = show;
    if (show) {
      const photo = document.createElement('img');
      photo.src = watch().caseback || labels.caseback;
      photo.decoding = 'async';
      photo.alt = `The ${watch().name} caseback.`;
      caseback.replaceChildren(photo);
    }
    image.hidden = show;
    caseback.hidden = !show;
    viewLabel.textContent = show ? `${watch().name.toUpperCase()} / ${labels.casebackLabel}` : labels.exploreLabel;
    setView(show ? 'caseback' : 'watch');
    syncTurnCta();
  }
  function setPackshot() {
    image.src = watch().packshot;
    image.alt = watch().packshotAlt;
    image.hidden = false;
    turned = false;
    caseback.hidden = true;
    syncTurnCta();
    viewLabel.textContent = labels.exploreLabel;
    setView('watch');
  }
  function setButtonLabel(button, text, arrow) {
    button.replaceChildren(document.createTextNode(`${text} `));
    const icon = document.createElement('span');
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = arrow;
    button.append(icon);
  }
  function syncChooseButton() {
    const chosen = WTL.selected === model;
    setButtonLabel(chooseButton, `${chosen ? labels.chosenPrefix : labels.choosePrefix} ${watch().name.toUpperCase()}`, chosen ? '✓' : '↗');
  }
  function setModel(side) {
    if (!data[side]) return;
    model = side;
    section.dataset.view = side;
    const current = watch();
    $$('[data-model]').forEach(button => {
      const active = button.dataset.model === side;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    });
    $('.model-panel').setAttribute('aria-labelledby', `tab-${side}`);
    $('[data-model-ref]').textContent = `REF. ${current.reference} / ${current.title.toUpperCase()}`;
    $('[data-model-name]').textContent = current.name.toUpperCase();
    $('[data-model-description]').textContent = current.description;
    $('[data-model-case]').textContent = current.caseSize;
    $('[data-model-pieces]').textContent = current.pieces;
    $('[data-model-price]').innerHTML = current.price;
    syncChooseButton();

    specs.replaceChildren();
    current.specs.forEach(([name, value, copy], index) => {
      const detail = document.createElement('details');
      detail.className = 'spec-detail';
      detail.id = `spec-${side}-${index}`;
      const summary = document.createElement('summary');
      [['spec-number', String(index + 1).padStart(2, '0')], ['spec-name', name], ['spec-plus', '+']].forEach(([className, text]) => {
        const span = document.createElement('span');
        span.className = className;
        span.textContent = text;
        if (className !== 'spec-name') span.setAttribute('aria-hidden', 'true');
        summary.append(span);
      });
      const body = document.createElement('p');
      const strong = document.createElement('strong');
      strong.className = 'spec-value';
      strong.textContent = value;
      body.append(strong, document.createTextNode(copy));
      detail.append(summary, body);
      detail.addEventListener('toggle', () => {
        if (!detail.isConnected) return;
        const fromWatch = detail.dataset.fromWatch === 'true';
        delete detail.dataset.fromWatch;
        if (!detail.open) {
          if (!specs.querySelector('details[open]')) setActive(null);
          return;
        }
        $$('.spec-detail').forEach(other => { if (other !== detail) other.open = false; });
        if (!fromWatch) setActive(index);
      });
      specs.append(detail);
    });
    setPackshot();
  }

  $('[data-close-part]').addEventListener('click', () => {
    card.hidden = true;
    overlay.querySelector(`[data-spec="${selected}"]`)?.focus({ preventScroll: true });
  }, { signal });
  turnButton.addEventListener('click', () => setCaseback(!turned), { signal });
  chooseButton.addEventListener('click', () => WTL.choose(model), { signal });
  image.addEventListener('load', scheduleFit, { signal });
  image.addEventListener('error', () => { overlay.setAttribute('hidden', ''); hideTip(); }, { signal });
  image.draggable = false;

  const sides = $$('[data-model]').map(button => button.dataset.model);
  $$('[data-model]').forEach(button => {
    button.addEventListener('click', () => setModel(button.dataset.model), { signal });
    button.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const at = sides.indexOf(model);
      const next = event.key === 'Home' ? sides[0]
        : event.key === 'End' ? sides[sides.length - 1]
        : sides[(at + (event.key === 'ArrowRight' ? 1 : sides.length - 1)) % sides.length];
      setModel(next);
      section.querySelector(`#tab-${next}`)?.focus();
    }, { signal });
  });

  let observer = null;
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(scheduleFit);
    [image, caseback, stage].forEach(element => observer.observe(element));
  } else addEventListener('resize', scheduleFit, { passive: true, signal });
  document.fonts?.ready.then(scheduleFit);

  document.addEventListener('wtl:allegiance', event => {
    if (data[event.detail.side]) setModel(event.detail.side);
    else syncChooseButton();
  }, { signal });

  setModel(WTL.selected && data[WTL.selected] ? WTL.selected : model);
  return () => { observer?.disconnect(); cancelAnimationFrame(layoutFrame); };
});

/* ============================================================
   DETAILS - the macro rail: side switch, renumbering, arrows,
   position dots and mouse-drag scrolling.
   ============================================================ */
WTL.register('macro', (section, signal) => {
  const rail = section.querySelector('.macro-rail');
  const prev = section.querySelector('[data-macro-prev]');
  const next = section.querySelector('[data-macro-next]');
  const dotsBox = section.querySelector('.macro-dots');
  const tiles = [...rail.querySelectorAll('.macro-tile')];
  const reduce = WTL.reducedMotion;

  function render(side) {
    section.dataset.macroSide = side;
    let n = 0;
    tiles.forEach(tile => {
      const shown = tile.dataset.side === side || tile.dataset.side === 'both';
      tile.hidden = !shown;
      if (shown) tile.querySelector('.macro-no').textContent = String(++n).padStart(2, '0');
    });
    section.querySelectorAll('[data-macro]').forEach(button =>
      button.setAttribute('aria-pressed', String(button.dataset.macro === side)));
    rail.scrollTo({ left: 0, behavior: 'smooth' });
    buildDots();
  }
  function step() {
    const shown = tiles.filter(tile => !tile.hidden);
    if (shown.length > 1) {
      const gap = shown[1].getBoundingClientRect().left - shown[0].getBoundingClientRect().left;
      if (gap > 1) return gap;
    }
    return shown.length ? shown[0].getBoundingClientRect().width : rail.clientWidth;
  }
  function maxScroll() { return Math.max(0, rail.scrollWidth - rail.clientWidth); }
  // one stop per card, plus a final stop flush with the end of the rail
  function positions() {
    const size = step(), end = maxScroll();
    if (end <= 1 || size <= 1) return [0];
    const stops = [];
    for (let x = 0; x < end - 8; x += size) stops.push(Math.round(x));
    stops.push(end);
    return stops;
  }
  function currentPage() {
    const stops = positions();
    let best = 0;
    stops.forEach((x, i) => {
      if (Math.abs(x - rail.scrollLeft) < Math.abs(stops[best] - rail.scrollLeft)) best = i;
    });
    return best;
  }
  function goTo(page) {
    const stops = positions();
    rail.scrollTo({ left: stops[Math.max(0, Math.min(stops.length - 1, page))], behavior: reduce.matches ? 'auto' : 'smooth' });
  }
  function syncControls() {
    const page = currentPage();
    [...dotsBox.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === page)));
    prev.disabled = rail.scrollLeft <= 1;
    next.disabled = rail.scrollLeft >= maxScroll() - 1;
  }
  function buildDots() {
    const total = positions().length;
    dotsBox.textContent = '';
    for (let i = 0; i < total; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'macro-dot';
      dot.setAttribute('aria-label', `Go to detail ${i + 1} of ${total}`);
      dot.addEventListener('click', () => goTo(i));
      dotsBox.appendChild(dot);
    }
    dotsBox.hidden = total < 2;
    syncControls();
  }

  prev.addEventListener('click', () => goTo(currentPage() - 1), { signal });
  next.addEventListener('click', () => goTo(currentPage() + 1), { signal });
  rail.addEventListener('keydown', event => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    goTo(currentPage() + (event.key === 'ArrowRight' ? 1 : -1));
  }, { signal });
  let frame = 0;
  rail.addEventListener('scroll', () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(syncControls);
  }, { passive: true, signal });
  addEventListener('resize', buildDots, { signal });
  section.querySelectorAll('[data-macro]').forEach(button =>
    button.addEventListener('click', () => render(button.dataset.macro), { signal }));

  // follow a sworn allegiance when there is one
  render(WTL.selected || 'saruman');
  document.addEventListener('wtl:allegiance', event => {
    if (event.detail.side !== section.dataset.macroSide) render(event.detail.side);
  }, { signal });

  // pointer drag, mouse only - touch keeps its native scrolling
  let down = false, x0 = 0, left0 = 0, moved = 0;
  rail.addEventListener('pointerdown', event => {
    if (event.pointerType === 'touch') return;
    down = true; moved = 0; x0 = event.clientX; left0 = rail.scrollLeft;
    rail.classList.add('is-drag');
    if (rail.setPointerCapture) rail.setPointerCapture(event.pointerId);
  }, { signal });
  rail.addEventListener('pointermove', event => {
    if (!down) return;
    const dx = event.clientX - x0;
    moved = Math.abs(dx);
    rail.scrollLeft = left0 - dx;
  }, { signal });
  ['pointerup', 'pointercancel', 'pointerleave'].forEach(name =>
    rail.addEventListener(name, () => { down = false; rail.classList.remove('is-drag'); }, { signal }));
  rail.addEventListener('click', event => {
    if (moved > 6) { event.preventDefault(); event.stopPropagation(); }
  }, { capture: true, signal });
  return () => cancelAnimationFrame(frame);
});

/* ============================================================
   EARLY ACCESS BENEFITS - once a side is sworn the seal points
   at the claim rather than back at the picker.
   ============================================================ */
WTL.register('launch-benefits', (section, signal) => {
  const go = section.querySelector('.lb-go');
  if (!go) return;
  function render() { go.setAttribute('href', WTL.selected ? go.dataset.sworn : go.dataset.unsworn); }
  render();
  document.addEventListener('wtl:allegiance', render, { signal });
});

/* ============================================================
   THE REALMS - the deck. One card stands, the other falls back;
   the read-out turns over when the standing card changes.
   ============================================================ */
WTL.register('houses', (section, signal) => {
  const cards = [...section.querySelectorAll('.hcard')];
  if (!cards.length) return;
  const dots = section.querySelector('.hdots');
  const go = section.querySelector('.hcopy__go');
  const copy = section.querySelector('.hcopy');
  const name = section.querySelector('.hcopy__n');
  const sub = section.querySelector('.hcopy__s');
  const text = section.querySelector('.hcopy__t');
  const reduce = WTL.reducedMotion.matches;
  let i = 0, busy = false, timer = 0;

  const dotEls = cards.map((card, k) => {
    const dot = document.createElement('button');
    dot.className = 'hdot' + (k === 0 ? ' is-on' : '');
    dot.type = 'button';
    dot.setAttribute('role', 'tab');
    dot.setAttribute('aria-label', card.dataset.name);
    dot.setAttribute('aria-selected', String(k === 0));
    dot.innerHTML = '<i></i>';
    dot.addEventListener('click', () => set(k));
    dots.appendChild(dot);
    return dot;
  });

  function paint() {
    const card = cards[i];
    name.textContent = card.dataset.name;
    sub.textContent = card.dataset.sub;
    text.textContent = card.dataset.text;
  }
  // the swap is a transform transition; busy only guards the text turn
  function set(n) {
    if (n === i || busy) return;
    i = n;
    cards.forEach((card, k) => {
      card.classList.toggle('is-on', k === i);
      card.classList.toggle('is-off', k !== i);
    });
    dotEls.forEach((dot, k) => {
      dot.classList.toggle('is-on', k === i);
      dot.setAttribute('aria-selected', String(k === i));
    });
    section.classList.remove('is-h0', 'is-h1');
    section.classList.add('is-h' + i);
    if (reduce) { paint(); return; }
    busy = true;
    copy.classList.add('is-turn');
    timer = setTimeout(() => { paint(); copy.classList.remove('is-turn'); busy = false; }, 380);
  }

  cards.forEach((card, k) => card.addEventListener('click', () => set(k), { signal }));
  go?.addEventListener('click', () => WTL.go(go.dataset.href), { signal });
  paint();
  return () => { clearTimeout(timer); dots.textContent = ''; };
});

/* ============================================================
   ALLEGIANCE - the vote bar. Percentages and the running count
   follow the sworn side.
   ============================================================ */
WTL.register('allegiance', (section, signal) => {
  const bar = section.querySelector('.res__bar');
  if (!bar) return;
  const base = { saruman: Number(section.dataset.baseSaruman) || 0, sauron: Number(section.dataset.baseSauron) || 0 };
  const el = name => section.querySelector(`[data-vote="${name}"]`);

  function render() {
    const side = WTL.selected;
    const counts = { ...base };
    if (side) counts[side] += 1;
    const total = counts.saruman + counts.sauron || 1;
    const s = counts.saruman / total * 100;
    const r = 100 - s;
    el('barS').style.width = s.toFixed(1) + '%';
    el('barR').style.width = r.toFixed(1) + '%';
    el('pctS').textContent = s.toFixed(1) + '%';
    el('pctR').textContent = r.toFixed(1) + '%';
    el('count').textContent = (counts.saruman + counts.sauron).toLocaleString('en-US');
    bar.setAttribute('aria-label', `Allegiance split, ${s.toFixed(1)} per cent ${WTL.sides.saruman.name} to ${r.toFixed(1)} per cent ${WTL.sides.sauron.name}`);
    const tail = el('tail');
    tail.textContent = side
      ? tail.dataset.sworn.replace('[name]', WTL.sides[side].name).replace('[title]', WTL.sides[side].title)
      : tail.dataset.unsworn;
    const status = section.querySelector('.allegiance-status');
    if (status && side) status.textContent = `YOUR ALLEGIANCE: ${WTL.sides[side].name.toUpperCase()} / ${WTL.sides[side].title.toUpperCase()}`;
  }
  render();
  document.addEventListener('wtl:allegiance', render, { signal });
});

/* ============================================================
   THE PALANTIR - press and hold. The stone opens first, the
   words arrive a beat later. A side already sworn is confirmed
   rather than re-rolled.
   ============================================================ */
WTL.register('palantir', (section, signal) => {
  const el = section.querySelector('.orb');
  const cv = section.querySelector('.orb canvas');
  if (!el || !cv) return;

  const reduce = WTL.reducedMotion.matches;
  const BASE_R = (Number(section.dataset.sauronChance) || 61) / 100;
  const HOLD = reduce ? 900 : 2800, OPEN = reduce ? 240 : 1700;
  const copy = section.dataset;
  const orb = { x: cv.getContext('2d'), on: false, t: 0, lt: 0, lt2: 0, px: .5, py: .5, verdict: null, hold: 0, holding: false, open: 0 };
  let alive = true;

  function chosen() {
    const side = WTL.selected;
    return side === 'saruman' ? 's' : side === 'sauron' ? 'r' : null;
  }
  function sideOf(code) { return code === 'r' ? 'sauron' : 'saruman'; }
  function tone(code) { section.dataset.side = code || ''; }

  const v = section.querySelector('.pal__verdict'),
    n = section.querySelector('[data-verdict-name]'),
    tx = section.querySelector('[data-verdict-text]'),
    swb = section.querySelector('.pal__swear'),
    spec = section.querySelector('.pal__cta'),
    lblS = el.querySelector('.orb__lbl s'),
    lblB = el.querySelector('.orb__lbl b');
  const IDLE = [lblS.textContent, lblB.textContent];
  const REST = { n: n.textContent, t: tx.textContent };
  let said = '', restore = 0;

  function say(a, b) {
    const key = a + '|' + b;
    if (key === said) return;
    said = key; lblS.textContent = a; lblB.textContent = b;
  }
  function stage(h) {
    if (h < .30) say(copy.hold1, copy.stage1);
    else if (h < .62) say(copy.hold1, copy.stage2);
    else if (h < .88) say(copy.hold2, copy.stage3);
    else say(copy.hold2, copy.stage4);
  }
  function clearVerdict() {
    v.classList.remove('is-s', 'is-r');
    n.textContent = REST.n; tx.textContent = REST.t;
    swb.hidden = true; spec.hidden = true;
    tone(chosen());
  }
  function press(e) {
    if (orb.holding) return;
    if (restore) { clearTimeout(restore); restore = 0; }
    if (orb.on) { orb.on = false; orb.open = 0; orb.verdict = null; el.classList.remove('is-open'); clearVerdict(); }
    orb.holding = true; el.classList.add('is-holding'); stage(orb.hold);
    if (e && e.pointerId !== undefined && el.setPointerCapture) {
      try { el.setPointerCapture(e.pointerId); } catch (_) {}
    }
  }
  function release() {
    if (!orb.holding) return;
    orb.holding = false; el.classList.remove('is-holding');
    if (!orb.on) {
      said = ''; say(copy.tooSoon1, copy.tooSoon2);
      restore = setTimeout(() => {
        restore = 0;
        if (!orb.holding && !orb.on) { said = ''; say(IDLE[0], IDLE[1]); }
      }, 1700);
    }
  }
  function reveal() {
    const sworn = chosen();
    const side = sworn || (Math.random() < BASE_R ? 'r' : 's');
    const name = WTL.sides[sideOf(side)].name;
    orb.holding = false; orb.hold = 1; orb.open = 0; orb.on = true; orb.verdict = side;
    el.classList.remove('is-holding'); el.classList.add('is-open');
    tone(side);
    setTimeout(() => {
      if (!orb.on || orb.verdict !== side) return;
      v.classList.remove('is-s', 'is-r'); v.classList.add('is-' + side);
      n.textContent = copy.verdict.replace('[name]', name);
      tx.textContent = side === 's' ? copy.textSaruman : copy.textSauron;
      spec.hidden = false;
      swb.dataset.side = side;
      swb.textContent = copy.swear.replace('[name]', name);
      swb.hidden = !!sworn;
      el.setAttribute('aria-label', `${copy.verdict.replace('[name]', name)}. Press and hold again to look once more.`);
      if (!reduce) {
        n.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], { duration: 760, easing: 'cubic-bezier(.16,1,.3,1)' });
        tx.animate([{ opacity: 0 }, { opacity: 0 }, { opacity: 1 }], { duration: 900, delay: 120, easing: 'ease-out' });
      }
    }, reduce ? 0 : 900);
  }

  el.addEventListener('pointermove', e => {
    const r = el.getBoundingClientRect();
    orb.px = (e.clientX - r.left) / r.width; orb.py = (e.clientY - r.top) / r.height;
  }, { signal });
  el.addEventListener('pointerdown', e => { e.preventDefault(); press(e); }, { signal });
  ['pointerup', 'pointercancel', 'pointerleave'].forEach(type => {
    el.addEventListener(type, () => { if (type === 'pointerleave') { orb.px = .5; orb.py = .5; } release(); }, { signal });
  });
  el.addEventListener('contextmenu', e => e.preventDefault(), { signal });
  addEventListener('blur', release, { signal });
  document.addEventListener('visibilitychange', () => { if (document.hidden) release(); }, { signal });
  el.addEventListener('keydown', e => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault(); if (e.repeat) return; press();
  }, { signal });
  el.addEventListener('keyup', e => { if (e.key === 'Enter' || e.key === ' ') release(); }, { signal });
  swb.addEventListener('click', () => {
    WTL.choose(sideOf(swb.dataset.side || orb.verdict));
    swb.hidden = true;
    WTL.go(spec.getAttribute('href'));
  }, { signal });
  document.addEventListener('wtl:allegiance', () => { if (!orb.on) tone(chosen()); }, { signal });
  tone(chosen());

  // --- the charge ticks on its own; the painter only reads it ---------
  function tick(now) {
    if (!alive) return;
    const dt = orb.lt2 ? Math.min(now - orb.lt2, 64) : 16; orb.lt2 = now;
    if (orb.holding && !orb.on) {
      orb.hold = Math.min(1, orb.hold + dt / HOLD); stage(orb.hold);
      if (orb.hold >= 1) reveal();
    } else if (!orb.on && orb.hold > 0) orb.hold = Math.max(0, orb.hold - dt / 620);
    else if (orb.on && orb.hold > 0) orb.hold = Math.max(0, orb.hold - dt / 900);
    if (orb.on && orb.open < 1) orb.open = Math.min(1, orb.open + dt / OPEN);
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);

  // --- the painter ----------------------------------------------------
  function drawOrb(t) {
    const dt = orb.lt ? Math.min(t - orb.lt, 64) : 16; orb.lt = t;
    orb.t += dt * (1 + orb.hold * 2.1);
    t = orb.t;
    const x = orb.x, S = 560, c = S / 2;
    x.clearRect(0, 0, S, S);

    const base = x.createRadialGradient(c - 70, c - 90, 20, c, c, c);
    base.addColorStop(0, '#2C1E11'); base.addColorStop(.55, '#120B05'); base.addColorStop(1, '#050302');
    x.fillStyle = base; x.beginPath(); x.arc(c, c, c - 2, 0, 6.2832); x.fill();

    x.save(); x.beginPath(); x.arc(c, c, c - 6, 0, 6.2832); x.clip();
    x.globalCompositeOperation = 'screen';
    const hot = orb.on, chg = orb.hold, boost = 1 + chg * 1.35;
    for (let i = 0; i < 26; i++) {
      const a = t * 0.00016 * (1 + i % 5 * .18) + i * 1.31, rad = (c * .22) + (i % 7) * (c * .085);
      const ox = c + Math.cos(a) * rad * .62 + (orb.px - .5) * 46;
      const oy = c + Math.sin(a * 1.23) * rad * .62 + (orb.py - .5) * 46;
      const rr = c * (.20 + (i % 4) * .055), g = x.createRadialGradient(ox, oy, 0, ox, oy, rr);
      if (hot && orb.verdict === 'r') {
        g.addColorStop(0, 'rgba(255,120,26,' + (.10 + .05 * Math.sin(t * .0008 + i)).toFixed(3) + ')');
        g.addColorStop(.5, 'rgba(168,42,22,.045)');
      } else if (hot && orb.verdict === 's') {
        g.addColorStop(0, 'rgba(247,227,182,' + (.075 + .035 * Math.sin(t * .0008 + i)).toFixed(3) + ')');
        g.addColorStop(.5, 'rgba(196,166,116,.035)');
      } else {
        g.addColorStop(0, 'rgba(' + (120 + chg * 70) + ',' + (124 + chg * 66) + ',' + (140 + chg * 50) + ',' +
          ((.055 + .03 * Math.sin(t * .0007 + i)) * boost).toFixed(3) + ')');
        g.addColorStop(.5, 'rgba(74,52,30,' + (.03 * boost).toFixed(3) + ')');
      }
      g.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = g; x.beginPath(); x.arc(ox, oy, rr, 0, 6.2832); x.fill();
    }

    if (!hot && chg > 0.001) {
      const ga = chg * chg, gr = x.createRadialGradient(c, c, 2, c, c, c * (.14 + .46 * chg));
      gr.addColorStop(0, 'rgba(247,227,182,' + (.30 * ga).toFixed(3) + ')');
      gr.addColorStop(.45, 'rgba(196,166,116,' + (.13 * ga).toFixed(3) + ')');
      gr.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = gr; x.beginPath(); x.arc(c, c, c * (.14 + .46 * chg), 0, 6.2832); x.fill();
    }

    if (hot) {
      const op = orb.open, pulse = .5 + .5 * Math.sin(t * .0022);
      const lift = (.74 + pulse * .26) * op, grow = .46 + .54 * op;
      if (orb.verdict === 'r') {
        const er = c * .88 * grow, eg = x.createRadialGradient(c, c, 5, c, c, er);
        eg.addColorStop(0, 'rgba(255,242,198,' + (.96 * lift).toFixed(3) + ')');
        eg.addColorStop(.11, 'rgba(255,186,58,' + (.86 * lift).toFixed(3) + ')');
        eg.addColorStop(.27, 'rgba(238,104,22,' + (.62 * lift).toFixed(3) + ')');
        eg.addColorStop(.52, 'rgba(178,42,10,' + (.30 * lift).toFixed(3) + ')');
        eg.addColorStop(1, 'rgba(84,12,3,0)');
        x.fillStyle = eg; x.beginPath(); x.arc(c, c, er, 0, 6.2832); x.fill();
        for (let f = 0; f < 48; f++) {
          const fa = f / 48 * 6.2832 + t * .00007, fl = c * (.30 + ((f * 37) % 11) / 11 * .34);
          x.strokeStyle = 'rgba(255,' + (120 + ((f * 53) % 80)) + ',30,' + ((.05 + .05 * Math.sin(t * .0013 + f)) * op).toFixed(3) + ')';
          x.lineWidth = c * .018; x.beginPath();
          x.moveTo(c + Math.cos(fa) * c * .14, c + Math.sin(fa) * c * .14);
          x.lineTo(c + Math.cos(fa) * fl, c + Math.sin(fa) * fl); x.stroke();
        }
        x.globalCompositeOperation = 'source-over';
        x.fillStyle = 'rgba(5,2,1,' + (.96 * op).toFixed(3) + ')';
        x.beginPath(); x.ellipse(c, c, c * .058 * grow, c * .40 * grow, 0, 0, 6.2832); x.fill();
      } else {
        const sr = c * .86 * grow, sg = x.createRadialGradient(c, c, 5, c, c, sr);
        sg.addColorStop(0, 'rgba(255,255,253,' + (.92 * lift).toFixed(3) + ')');
        sg.addColorStop(.13, 'rgba(244,241,232,' + (.60 * lift).toFixed(3) + ')');
        sg.addColorStop(.36, 'rgba(206,204,196,' + (.26 * lift).toFixed(3) + ')');
        sg.addColorStop(1, 'rgba(150,154,160,0)');
        x.fillStyle = sg; x.beginPath(); x.arc(c, c, sr, 0, 6.2832); x.fill();
        x.globalCompositeOperation = 'source-over';
        x.strokeStyle = 'rgba(14,14,18,' + (.78 * op).toFixed(3) + ')'; x.lineCap = 'round';
        x.lineWidth = c * .044;
        x.beginPath(); x.moveTo(c, c - c * .14 * grow); x.lineTo(c, c + c * .32 * grow); x.stroke();
        x.lineWidth = c * .036;
        for (let k = -2; k <= 2; k++) {
          const ka = -Math.PI / 2 + k * 0.33; x.beginPath();
          x.moveTo(c + Math.cos(ka) * c * .12 * grow, c + Math.sin(ka) * c * .12 * grow);
          x.lineTo(c + Math.cos(ka) * c * .42 * grow, c + Math.sin(ka) * c * .42 * grow); x.stroke();
        }
        x.lineCap = 'butt';
      }
    }
    x.restore();

    x.globalCompositeOperation = 'source-over';
    const hl = x.createRadialGradient(c - c * .46, c - c * .54, 4, c - c * .46, c - c * .54, c * .46);
    hl.addColorStop(0, 'rgba(255,255,255,.13)'); hl.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = hl; x.beginPath(); x.arc(c, c, c - 4, 0, 6.2832); x.fill();
    x.strokeStyle = 'rgba(242,230,206,.10)'; x.lineWidth = 2;
    x.beginPath(); x.arc(c, c, c - 3, 0, 6.2832); x.stroke();

    if (orb.hold > .002) {
      const hv = orb.hold; x.lineCap = 'round'; x.lineWidth = 3.5;
      x.strokeStyle = orb.on
        ? (orb.verdict === 'r' ? 'rgba(255,130,0,' + (.55 * hv).toFixed(3) + ')' : 'rgba(240,220,174,' + (.55 * hv).toFixed(3) + ')')
        : 'rgba(242,230,206,' + (.16 + .46 * hv).toFixed(3) + ')';
      x.beginPath(); x.arc(c, c, c - 4, -1.5708, -1.5708 + 6.2832 * hv); x.stroke();
      x.lineCap = 'butt';
    }
  }

  // paint only while the stone is on screen
  let onScreen = true;
  let observer = null;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(entries => {
      onScreen = entries.some(entry => entry.isIntersecting);
      if (!onScreen) release();
    }, { threshold: 0 });
    observer.observe(el);
  }
  function master(t) {
    if (!alive) return;
    if (onScreen) drawOrb(t); else orb.lt = t;
    requestAnimationFrame(master);
  }
  requestAnimationFrame(master);

  return () => { alive = false; clearTimeout(restore); observer?.disconnect(); };
});

/* ============================================================
   EARLY ACCESS - the waitlist. A Shopify customer form: the
   sworn side is sent as a customer tag.
   ============================================================ */
WTL.register('early-access', (section, signal) => {
  const form = section.querySelector('form');
  if (!form) return;
  const message = section.querySelector('.form-message');
  const tags = form.querySelector('[data-wtl-tags]');
  const submit = form.querySelector('[type="submit"]');

  function sync() {
    const side = WTL.selected;
    if (!side) return;
    tags.value = [tags.dataset.base, `${tags.dataset.base}-${side}`].filter(Boolean).join(', ');
    submit.setAttribute('aria-label', `${submit.textContent.trim()} for ${WTL.sides[side].name}`);
    if (message && !message.dataset.keep) message.textContent = '';
  }
  form.addEventListener('submit', event => {
    if (section.dataset.requireSide === 'true' && !WTL.selected) {
      event.preventDefault();
      delete message.dataset.keep;
      message.textContent = message.dataset.chooseFirst;
      form.querySelector('.form-choices button')?.focus();
    }
  }, { signal });
  document.addEventListener('wtl:allegiance', () => { if (message) delete message.dataset.keep; sync(); }, { signal });
  sync();
});

/* ============================================================
   HEADER - sticky below the theme header. The header sits over
   the top of the hero, and once it reaches the theme's own header
   while scrolling, it is fixed just beneath it. The theme header's
   height is measured live, so sticky, hide-on-scroll headers and
   announcement bars are followed.
   ============================================================ */
WTL.register('header', (anchor, signal) => {
  const header = anchor.querySelector('.header');
  const mode = anchor.dataset.position;
  const onLanding = document.body.classList.contains('wtl-landing');
  if (!header || anchor.dataset.sticky !== 'true' || mode === 'fixed' || (mode === 'auto' && onLanding)) return;

  const manual = anchor.dataset.offset === '' ? NaN : Number(anchor.dataset.offset);
  const BARS = 'header, sticky-header, [id*="header" i], [class*="header" i], [class*="announcement" i]';
  const STUCK_Z = 7;
  let bars = [];
  const raised = new Map();

  // The theme's fixed or sticky bars at the top of the page, outside the WTL sections.
  function collect() {
    bars = [...document.querySelectorAll(BARS)].filter(el =>
      !el.closest('.wtl-s') && ['fixed', 'sticky'].includes(getComputedStyle(el).position));
    // keep the theme header (and its menus) above the stuck WTL header
    bars.forEach(el => {
      const z = Number(getComputedStyle(el).zIndex);
      if (!raised.has(el) && (!Number.isFinite(z) || z <= STUCK_Z)) {
        raised.set(el, el.style.zIndex);
        el.style.zIndex = String(STUCK_Z + 1);
      }
    });
  }
  function offset() {
    if (Number.isFinite(manual)) return manual;
    let bottom = 0;
    bars.forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.height && r.height < 400 && r.top < 60 && r.bottom > bottom) bottom = r.bottom;
    });
    return Math.max(0, Math.round(bottom));
  }
  let frame = 0;
  function update() {
    frame = 0;
    const top = offset();
    header.style.setProperty('--wtl-stick-top', `${top}px`);
    header.classList.toggle('is-stuck', anchor.getBoundingClientRect().top <= top);
  }
  function schedule() { if (!frame) frame = requestAnimationFrame(update); }

  collect();
  update();
  addEventListener('scroll', schedule, { passive: true, signal });
  addEventListener('resize', () => { collect(); schedule(); }, { passive: true, signal });
  document.addEventListener('shopify:section:load', () => { collect(); schedule(); }, { signal });
  // theme headers often animate in and out; follow the end of those transitions too
  document.addEventListener('transitionend', schedule, { signal });

  return () => {
    cancelAnimationFrame(frame);
    header.classList.remove('is-stuck');
    raised.forEach((z, el) => { el.style.zIndex = z; });
  };
});

/* ============================================================
   ALLEGIANCE BAR - the sticky bar and the toast are position:
   fixed. Themes often put transforms, filters or
   content-visibility on <main> or on sections (page transitions,
   scroll reveals), which traps fixed elements inside them, so
   both are moved straight under <body>.
   ============================================================ */
WTL.register('choice-bar', root => {
  const holder = document.createElement('div');
  holder.className = 'wtl-s wtl-portal';
  holder.dataset.wtlPortal = 'choice-bar';
  holder.append(...root.children);
  document.body.append(holder);
  WTL.refresh();
  return () => holder.remove();
});